import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import api from "services/api";
import { PDFDocument } from "pdf-lib/dist/pdf-lib.esm.min.js";

const createEmptyMapping = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  PdfFieldName: "",
  DataFieldKey: "",
  DataPath: "",
  IsRequired: false,
  MappingSource: "manual",
});

const LINGUIST_FIELD_OPTIONS = [
  { key: "Name", label: "Name" },
  { key: "Initials", label: "Initials" },
  { key: "Address", label: "Address" },
  { key: "PhoneNumber", label: "Phone Number" },
  { key: "HourlyRate", label: "Hourly Rate" },
  { key: "MinimumHours", label: "Minimum Hours" },
  { key: "Email", label: "Email" },
];

const REQUIREMENT_NA_VALUE = "N/A";

const normalizeText = (value) => String(value || "")
  .toLowerCase()
  .replace(/[^a-z0-9]/g, "");

const getAutoMappedLinguistField = (pdfFieldName) => {
  const key = normalizeText(pdfFieldName);
  if (!key) return "";

  if (key.includes("signature") || key.includes("date")) return "";
  if (key.includes("initial")) return "Initials";
  if (key.includes("phonenumber") || (key.includes("phone") && key.includes("signer"))) return "PhoneNumber";
  if (key.includes("email")) return "Email";
  if (key.includes("minimum")) return "MinimumHours";
  if (key.includes("rate")) return "HourlyRate";
  if (key.includes("fullname") || key.startsWith("name") || key.includes("namesigner")) return "Name";
  if (key.includes("address")) return "Address";

  return "";
};

const findRequirementOptionValue = (options, aliases) => {
  const normalizedAliases = aliases.map((alias) => normalizeText(alias)).filter(Boolean);
  if (normalizedAliases.length === 0) return "";

  for (const option of options) {
    const optionValue = normalizeText(option?.value);
    const optionLabel = normalizeText(option?.label);
    if (normalizedAliases.some((alias) => alias === optionValue || alias === optionLabel)) {
      return option.value;
    }
  }

  for (const option of options) {
    const optionValue = normalizeText(option?.value);
    const optionLabel = normalizeText(option?.label);
    if (normalizedAliases.some((alias) => optionValue.includes(alias) || optionLabel.includes(alias))) {
      return option.value;
    }
  }

  return "";
};

const getAutoLinkedRequirementValue = (templateName, requirementOptions) => {
  const normalizedName = normalizeText(templateName);
  if (!normalizedName) return "";

  if (normalizedName.includes("celinguistcontractappendix")) {
    return REQUIREMENT_NA_VALUE;
  }

  if (normalizedName.includes("w9")) {
    return findRequirementOptionValue(requirementOptions, ["W9"]);
  }

  if (normalizedName.includes("arrestorconvictionform")) {
    return findRequirementOptionValue(requirementOptions, ["Arrest Conviction Form", "ArrestConvictionForm"]);
  }

  if (normalizedName.includes("celinguistcontract")) {
    return findRequirementOptionValue(requirementOptions, ["Contract"]);
  }

  if (normalizedName.includes("linguistdirectdepositauthorizationform")) {
    return findRequirementOptionValue(requirementOptions, ["Direct Deposit Authorization", "DirectDepositAuthorization"]);
  }

  if (normalizedName.includes("linguisttrainingacknowledgement")) {
    return findRequirementOptionValue(requirementOptions, ["Training Acknowledgement", "TrainingAcknowledgement"]);
  }

  if (normalizedName.includes("medicalinterpreteronlinetmio") || normalizedName.includes("tmio")) {
    return findRequirementOptionValue(requirementOptions, ["The Medical Interpreter Online"]);
  }

  return "";
};

const DocumentTemplateNewEdit = () => {
  usePageTitle("Document Template");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.templateId;
  const existingTemplate = overlayData?.template || null;

  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "documentTemplate",
    isEditMode ? overlayData?.templateId : null,
  );

  const [saving, setSaving] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [loadingPdfFields, setLoadingPdfFields] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [name, setName] = useState(existingTemplate?.Name || "");
  const [linkedRequirementColumn, setLinkedRequirementColumn] = useState(
    existingTemplate?.LinkedRequirementColumn || "",
  );
  const [isActive, setIsActive] = useState(
    existingTemplate?.IsActive === undefined ? true : !!existingTemplate?.IsActive,
  );
  const [mappings, setMappings] = useState([]);
  const [fetchedPdfFields, setFetchedPdfFields] = useState([]);
  const [requirementOptions, setRequirementOptions] = useState([]);

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    let mounted = true;

    const loadRequirementOptions = async () => {
      try {
        const response = await api.get("/linguists/requirements");
        if (!mounted) return;

        const options = (response.data || [])
          .map((definition) => ({
            value: String(definition?.SourceColumn || "").trim(),
            label: String(definition?.Name || definition?.SourceColumn || "").trim(),
          }))
          .filter((item) => item.value)
          .sort((a, b) => a.label.localeCompare(b.label));

        setRequirementOptions(options);
      } catch (err) {
        console.error("Error loading linguist requirements:", err);
        showSnackbar("Could not load linguist requirements", theme, "error");
      }
    };

    loadRequirementOptions();

    return () => {
      mounted = false;
    };
  }, [showSnackbar, theme]);

  useEffect(() => {
    if (!isEditMode) return;
    setLinkedRequirementColumn(existingTemplate?.LinkedRequirementColumn || "");
  }, [isEditMode, existingTemplate?.LinkedRequirementColumn]);

  useEffect(() => {
    if (!isEditMode || !overlayData?.templateId) return;

    let mounted = true;
    const loadMappings = async () => {
      setLoadingMappings(true);
      try {
        const response = await api.get(`/documentTemplates/${overlayData.templateId}/mappings`);
        if (!mounted) return;

        const incoming = (response.data || []).map((mapping) => ({
          id: mapping.id,
          PdfFieldName: mapping.PdfFieldName || "",
          DataFieldKey: mapping.DataFieldKey || "",
          DataPath: mapping.DataPath || "",
          IsRequired: mapping.IsRequired === true,
          MappingSource: "manual",
        }));

        setMappings(incoming);
      } catch (err) {
        console.error("Error loading document mappings:", err);
        if (mounted) {
          showSnackbar("Could not load template fields", theme, "error");
        }
      } finally {
        if (mounted) setLoadingMappings(false);
      }
    };

    loadMappings();

    return () => {
      mounted = false;
    };
  }, [isEditMode, overlayData?.templateId, showSnackbar, theme]);

  useEffect(() => {
    const extractPdfFieldNames = async () => {
      setLoadingPdfFields(true);

      try {
        let bytes;

        if (!isEditMode && selectedFile) {
          bytes = await selectedFile.arrayBuffer();
        } else if (isEditMode && overlayData?.templateId) {
          const response = await api.get(
            `/documentTemplates/${overlayData.templateId}/file?mode=view`,
            { responseType: "blob" },
          );
          bytes = await response.data.arrayBuffer();
        }

        if (!bytes) {
          setFetchedPdfFields([]);
          return;
        }

        const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const form = pdfDoc.getForm();
        const fieldNames = Array.from(
          new Set(form.getFields().map((field) => String(field.getName() || "").trim()).filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b));

        setFetchedPdfFields(fieldNames);
      } catch (err) {
        console.error("Error extracting PDF fields:", err);
        setFetchedPdfFields([]);
        showSnackbar("Could not fetch fields from PDF", theme, "error");
      } finally {
        setLoadingPdfFields(false);
      }
    };

    extractPdfFieldNames();
  }, [isEditMode, selectedFile, overlayData?.templateId, showSnackbar, theme]);

  useEffect(() => {
    if (isEditMode) return;
    if (String(linkedRequirementColumn || "").trim()) return;

    const autoRequirementValue = getAutoLinkedRequirementValue(name, requirementOptions);
    if (autoRequirementValue) {
      setLinkedRequirementColumn(autoRequirementValue);
    }
  }, [isEditMode, linkedRequirementColumn, name, requirementOptions]);

  const canSave = useMemo(() => {
    return !!name.trim() && !!String(linkedRequirementColumn || "").trim();
  }, [name, linkedRequirementColumn]);

  const validMappings = useMemo(() => {
    return mappings.filter(
      (mapping) =>
        String(mapping.PdfFieldName || "").trim() && String(mapping.DataFieldKey || "").trim(),
    );
  }, [mappings]);

  const hasInvalidMappings = useMemo(() => {
    return false;
  }, []);

  const emitSettingsUpdate = (changeAction, recordId) => {
    if (!socket) return;
    socket.emit("settings:saved", {
      settingsType: "DocumentTemplate",
      recordId,
      changeAction,
      user: {
        id: currentUser?.id,
        name: currentUser?.Name,
        imageThumbnail: currentUser?.ImageThumbnail,
      },
    });
  };

  const handleFileSelection = (event) => {
    const file = event?.target?.files?.[0] || null;
    if (!file) return;

    if (file.type !== "application/pdf") {
      showSnackbar("Only PDF files are allowed", theme, "error");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showSnackbar("Max file size is 2MB", theme, "error");
      return;
    }

    setSelectedFile(file);
    if (!name.trim()) {
      const baseName = file.name.replace(/\.pdf$/i, "");
      setName(baseName);
    }
  };

  const getMappingValueForPdfField = (pdfFieldName) => {
    const match = mappings.find(
      (mapping) => String(mapping.PdfFieldName || "").trim() === pdfFieldName,
    );
    return match ? String(match.DataFieldKey || "").trim() : "";
  };

  const setMappingValueForPdfField = (pdfFieldName, selectedDataFieldKey) => {
    const normalizedPdfFieldName = String(pdfFieldName || "").trim();
    const normalizedDataFieldKey = String(selectedDataFieldKey || "").trim();

    setMappings((previous) => {
      const withoutField = previous.filter(
        (mapping) => String(mapping.PdfFieldName || "").trim() !== normalizedPdfFieldName,
      );

      if (!normalizedPdfFieldName || !normalizedDataFieldKey) {
        return withoutField;
      }

      return [
        ...withoutField,
        {
          ...createEmptyMapping(),
          PdfFieldName: normalizedPdfFieldName,
          DataFieldKey: normalizedDataFieldKey,
          MappingSource: "manual",
        },
      ];
    });
  };

  const suggestedMappingByField = useMemo(() => {
    const suggestions = {};
    fetchedPdfFields.forEach((fieldName) => {
      const suggestedField = getAutoMappedLinguistField(fieldName);
      if (suggestedField) {
        suggestions[fieldName] = suggestedField;
      }
    });
    return suggestions;
  }, [fetchedPdfFields]);

  const suggestedMappingCount = useMemo(
    () => Object.keys(suggestedMappingByField).length,
    [suggestedMappingByField],
  );

  const applySuggestedMappings = useCallback(() => {
    setMappings((previous) => {
      const byField = new Map(
        previous.map((mapping) => [String(mapping?.PdfFieldName || "").trim(), mapping]),
      );
      const next = [...previous];

      Object.entries(suggestedMappingByField).forEach(([pdfFieldName, dataFieldKey]) => {
        const existing = byField.get(pdfFieldName);

        if (existing && String(existing.DataFieldKey || "").trim()) {
          return;
        }

        if (existing) {
          const updated = {
            ...existing,
            DataFieldKey: dataFieldKey,
            MappingSource: "suggested",
          };
          const index = next.findIndex((item) => item.id === existing.id);
          if (index >= 0) next[index] = updated;
          return;
        }

        next.push({
          ...createEmptyMapping(),
          PdfFieldName: pdfFieldName,
          DataFieldKey: dataFieldKey,
          MappingSource: "suggested",
        });
      });

      return next;
    });
  }, [suggestedMappingByField]);

  const confirmSuggestedMappings = () => {
    setMappings((previous) =>
      previous.map((mapping) => {
        const hasMappedValue = Boolean(String(mapping?.DataFieldKey || "").trim());
        if (!hasMappedValue) return mapping;
        if (mapping?.MappingSource !== "suggested") return mapping;
        return {
          ...mapping,
          MappingSource: "manual",
        };
      }),
    );
  };

  const pendingSuggestedCount = useMemo(
    () =>
      mappings.filter(
        (mapping) =>
          mapping?.MappingSource === "suggested" &&
          Boolean(String(mapping?.DataFieldKey || "").trim()),
      ).length,
    [mappings],
  );

  useEffect(() => {
    if (isEditMode || fetchedPdfFields.length === 0) return;
    applySuggestedMappings();
  }, [fetchedPdfFields, isEditMode, applySuggestedMappings]);

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Template name is required", theme, "error");
      return;
    }

    if (!String(linkedRequirementColumn || "").trim()) {
      showSnackbar("Linked requirement is required", theme, "error");
      return;
    }

    const linkedRequirementValue =
      linkedRequirementColumn === REQUIREMENT_NA_VALUE
        ? REQUIREMENT_NA_VALUE
        : String(linkedRequirementColumn || "").trim();

    let failingStep = "save";

    setSaving(true);
    try {
      let templateId = overlayData?.templateId;
      let changeAction = "Updated";

      if (!isEditMode) {
        failingStep = "upload template";
        if (!selectedFile) {
          showSnackbar("Upload a PDF to continue", theme, "error");
          setSaving(false);
          return;
        }

        const formData = new FormData();
        formData.append("name", name.trim());
        formData.append("LinkedRequirementColumn", linkedRequirementValue);
        formData.append("file", selectedFile);

        const uploadResponse = await api.post("/documentTemplates/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        templateId = uploadResponse?.data?.id;
        changeAction = "Created";
      } else {
        failingStep = "update template";
        await api.put(`/documentTemplates/${templateId}`, {
          Name: name.trim(),
          IsActive: isActive,
          LinkedRequirementColumn: linkedRequirementValue,
        });
      }

      if (!templateId) {
        throw new Error("Template ID missing after save");
      }

      failingStep = "save template mappings";
      await api.put(`/documentTemplates/${templateId}/mappings`, {
        mappings: validMappings.map((mapping) => ({
          PdfFieldName: String(mapping.PdfFieldName || "").trim(),
          DataFieldKey: String(mapping.DataFieldKey || "").trim(),
          DataPath: "",
          IsRequired: false,
        })),
      });

      if (!isEditMode && !isActive) {
        await api.put(`/documentTemplates/${templateId}`, {
          Name: name.trim(),
          IsActive: false,
          LinkedRequirementColumn: linkedRequirementValue,
        });
      }

      showSnackbar(
        isEditMode ? "Document template updated" : "Document template created",
        theme,
        "success",
      );

      emitSettingsUpdate(changeAction, templateId);
      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      console.error(`Error saving document template at step: ${failingStep}`, err);
      handleErrorDialog(
        err?.response?.data?.error || err?.response?.data?.details || err?.message || `Error during ${failingStep}`,
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isEditMode ? "Edit Document Template" : "New Document Template";
  const isNewWithoutFile = !isEditMode && !selectedFile;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "92%", md: "80%", lg: "70%" },
          minHeight: { xs: "100%", sm: "600px" },
          maxWidth: "1100px",
          maxHeight: { xs: "100%", sm: "86%" },
          display: "flex",
          borderRadius: { xs: 0, sm: "1.5rem" },
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: { xs: 0, sm: "0rem .2rem 1.5rem 0" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: { xs: "0.5rem 0.75rem", sm: "0.5rem 2rem" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: { xs: "auto", sm: "60%" },
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: { xs: "auto", sm: "40%" },
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{ borderRadius: "12px" }}
              >
                Discard
              </Button>
            )}

            <Tooltip title={isMobile && saving ? "Saving..." : ""} placement="top">
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                      disabled={saving || !canSave || hasInvalidMappings}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || !canSave || hasInvalidMappings}
                    sx={{ borderRadius: "12px" }}
                  >
                    Save
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: { xs: "0.75rem", sm: "1rem 2.3rem 1rem 2.5rem" },
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            {isNewWithoutFile ? (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    minHeight: "420px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button variant="outlined" component="label">
                    Select PDF File
                    <input type="file" hidden accept="application/pdf" onChange={handleFileSelection} />
                  </Button>
                </Box>
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      Template Name
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Switch
                        checked={isActive}
                        onChange={(event) => setIsActive(event.target.checked)}
                      />
                      <Typography variant="body2">
                        {isActive ? "Active" : "Archived"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Template Name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="e.g., Linguist Agreement"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Linked Linguist Requirement"
                    value={linkedRequirementColumn}
                    onChange={(event) => setLinkedRequirementColumn(event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select requirement
                    </MenuItem>
                    <MenuItem value={REQUIREMENT_NA_VALUE}>N/A</MenuItem>
                    {requirementOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {!isEditMode && !selectedFile && (
                  <Grid size={{ xs: 12 }}>
                    <Button variant="outlined" component="label">
                      Select PDF File
                      <input type="file" hidden accept="application/pdf" onChange={handleFileSelection} />
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    File: {selectedFile?.name || existingTemplate?.OriginalFileName || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Template Fields Mapping
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.primary.border2}`,
                      borderRadius: "10px",
                      p: 1.5,
                      bgcolor: theme.palette.background.default,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1.25,
                      }}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        Fields Fetched from PDF ({fetchedPdfFields.length})
                      </Typography>
                      {!isEditMode && suggestedMappingCount > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={confirmSuggestedMappings}
                          disabled={pendingSuggestedCount === 0}
                        >
                          Confirm Suggested Mappings ({pendingSuggestedCount})
                        </Button>
                      )}
                    </Box>

                    {loadingPdfFields ? (
                      <Typography variant="body2" color="text.secondary">
                        Fetching PDF fields...
                      </Typography>
                    ) : fetchedPdfFields.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No PDF fields found in this file.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          maxHeight: 350,
                          overflowY: "auto",
                          display: "grid",
                          gap: 1,
                        }}
                      >
                        {fetchedPdfFields.map((fieldName) => {
                          const selectedDataFieldKey = getMappingValueForPdfField(fieldName);
                          const fieldMapping = mappings.find(
                            (mapping) => String(mapping?.PdfFieldName || "").trim() === fieldName,
                          );
                          const hasMappedValue = Boolean(String(selectedDataFieldKey || "").trim());
                          const isSuggestedMapping = hasMappedValue && fieldMapping?.MappingSource === "suggested";
                          const textFieldBorderColor = hasMappedValue
                            ? (isSuggestedMapping ? theme.palette.info.main : theme.palette.primary.main)
                            : theme.palette.primary.border2;

                          return (
                            <Box
                              key={fieldName}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 280px" },
                                gap: 1,
                                alignItems: "center",
                                p: 0.75,
                                borderRadius: "8px",
                                border: `1px solid ${theme.palette.primary.border2}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  minWidth: 0,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {fieldName}
                              </Typography>
                              <TextField
                                select
                                size="small"
                                label="Linguist Column"
                                value={selectedDataFieldKey}
                                onChange={(event) => setMappingValueForPdfField(fieldName, event.target.value)}
                                sx={{
                                  width: { xs: "100%", md: "280px !important" },
                                  m: "0 !important",
                                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                    borderColor: textFieldBorderColor,
                                  },
                                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: textFieldBorderColor,
                                  },
                                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: textFieldBorderColor,
                                  },
                                }}
                              >
                                <MenuItem value="">Leave Empty</MenuItem>
                                {LINGUIST_FIELD_OPTIONS.map((option) => (
                                  <MenuItem key={option.key} value={option.key}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {loadingMappings ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading existing fields...
                    </Typography>
                  ) : (
                    <Alert severity="info">
                      Choose a Linguist column for each PDF field, or leave it empty to keep that PDF field blank.
                    </Alert>
                  )}
                </Grid>
              </>
            )}
          </Grid>

        </Box>
      </Box>

      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default DocumentTemplateNewEdit;
